import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import { saveVaccineConsultation } from "../../redux/slices/vaccineConsultationSlice";

function VaccineAssessmentModal({ showConsultation, handleClose, form, questions, setIsLoading }) {
  const dispatch = useDispatch();
  const vaccineConsultation = useSelector(state => state.vaccineConsultation);
  const [answers, setAnswers] = useState(vaccineConsultation.answers || []);
  const [explanations, setExplanations] = useState(vaccineConsultation.explanations || []);
  const [errors, setErrors] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);



  const handleAnswer = (questionId, optionId, text) => {
    const question = questions.find(q => q._id === questionId);
    if (!question) return;

    const current = questions.findIndex(q => q._id === questionId);

    setErrors((prev) => {
      const updatedErrors = { ...prev };
      delete updatedErrors[current];
      return updatedErrors;
    });

    const newAnswer = answers[current]
      ? { ...answers[current] }
      : {
        _id: question._id,
        options: [],
        description: "",
      };

    // Handle answers based on input type
    if (question.inputType === "radio" || question.inputType === "select") {
      newAnswer.options = [optionId];
    } else if (question.inputType === "checkbox") {
      const currentOptions = newAnswer.options || [];
      const isExclusive = question.options.find(
        (opt) => opt._id === optionId
      )?.exclusive;

      if (isExclusive) {
        newAnswer.options = [optionId];
      } else {
        const filteredOptions = currentOptions.filter(
          (opt) =>
            !question.options.find((option) => option._id === opt)?.exclusive
        );

        if (currentOptions.includes(optionId)) {
          newAnswer.options = filteredOptions.filter(
            (opt) => opt !== optionId
          );
        } else {
          newAnswer.options = [...filteredOptions, optionId];
        }
      }
    } else if (question.inputType === "text") {
      newAnswer.description = text;
    }

    // Update answers state
    setAnswers((prev) => {
      const updatedAnswers = [...prev];
      updatedAnswers[current] = newAnswer;
      return updatedAnswers;
    });

    const hasCorrectOptions = question.options.some(
      (option) => option.isCorrectAnswer === true
    );

    if (hasCorrectOptions) {
      const incorrectOption = newAnswer.options.find(
        (optId) =>
          question.options.find((option) => option._id === optId)
            ?.isCorrectAnswer === false
      );

      if (incorrectOption) {
        let error = question?.warning
          ? question.warning
          : "Your answer is incorrect. Please check your selection.";
        setErrors((prev) => ({
          ...prev,
          [current]: error,
        }));
      } else {
        setErrors((prev) => {
          const updatedErr = { ...prev };
          delete updatedErr[current];
          return updatedErr;
        });
      }
    } else {
      setErrors((prev) => {
        const updatedErr = { ...prev };
        delete updatedErr[current];
        return updatedErr;
      });
    }

    if (question.inputType !== "text") {
      const requiresExplanation = newAnswer.options.some(
        (optId) =>
          question.options.find((option) => option._id === optId)
            ?.requireExplanation
      );

      setExplanations((prev) => {
        let updatedExplanations = [...prev];

        if (requiresExplanation) {
          const existingExplanation = updatedExplanations.find(
            (ex) => ex._id === question._id
          );

          if (!existingExplanation) {
            updatedExplanations.push({ _id: question._id, text: "" });
          }
        } else {
          updatedExplanations = updatedExplanations.filter(
            (ex) => ex._id !== question._id
          );
        }

        return updatedExplanations;
      });
    }
  };

  const handleExplanationChange = (questionId, text) => {
    setExplanations(prev => {
      const updated = [...prev];
      const existingIndex = updated.findIndex(ex => ex._id === questionId);

      if (existingIndex >= 0) {
        updated[existingIndex] = { ...updated[existingIndex], text };
      } else {
        updated.push({ _id: questionId, text });
      }

      return updated;
    });
  };

  const renderQuestionInput = (question, index) => {
    const answer = answers[index] || { options: [], description: "" };
    const questionError = errors[index];

    switch (question.inputType) {
      case "radio":
        return (
          <div className="mb-10">
            <h6>{question.title}</h6>
            <div>
              {question.options.map((option) => (
                <div className="form-checks mt-3" key={option._id}>
                  <input
                    type="radio"
                    name={`question-${question._id}`}
                    id={`option-${option._id}`}
                    className="input-section op-0"
                    checked={answer.options.includes(option._id)}
                    onChange={() => handleAnswer(question._id, option._id, '')}
                  />
                  <label className={`padc bullet w-100 d-flex align-items-center gap-3 ${answer.options.includes(option._id) ? 'active-check' : ''}`}>
                    <div className={`label-cs`}></div>
                    <span className="metropill">{option.optionText}</span>
                  </label>
                </div>
              ))}
            </div>
            {questionError && <div className="text-danger mt-2">{renderErrorSection()}</div>}
          </div>
        );

      case "checkbox":
        return (
          <div className="mb-10">
            <h6>{question.title}</h6>
            <div>
              {question.options.map((option) => (
                <div className="form-checks mt-3" key={option._id}>
                  <input
                    type="checkbox"
                    name={`question-${question._id}`}
                    id={`option-${option._id}`}
                    className="input-section op-0"
                    checked={answer.options.includes(option._id)}
                    onChange={() => handleAnswer(question._id, option._id, '')}
                  />
                  <label className={`padc bullet w-100 d-flex align-items-center gap-3 ${answer.options.includes(option._id) ? 'active-check' : ''}`}>
                    <div className={`label-check flex-shrink-0`}></div>
                    <span className="metropill">{option.optionText}</span>
                  </label>
                </div>
              ))}
            </div>
            {questionError && <div className="text-danger mt-2">{renderErrorSection()}</div>}
          </div>
        );

      case "text":
        return (
          <div className="mb-10">
            <h6>{question.title}</h6>
            <input
              type="text"
              className="input_text metropill"
              placeholder={question.title}
              value={answer.description || ""}
              onChange={(e) => handleAnswer(question._id, null, e.target.value)}
            />
          </div>
        );

      case "select":
        return (
          <div className="mb-10">
            <h6>{question.title}</h6>
            <select
              className="form-select metropill"
              value={answer.options[0] || ""}
              onChange={(e) => handleAnswer(question._id, e.target.value)}
            >
              <option value="">Select an option</option>
              {question.options.map((option) => (
                <option key={option._id} value={option._id}>
                  {option.optionText}
                </option>
              ))}
            </select>
            {questionError && <div className="text-danger mt-2">{renderErrorSection()}</div>}
          </div>
        );

      default:
        return null;
    }
  };

  const renderExplanationField = (question, index) => {
    if (question.inputType === "text") return null;

    const answer = answers[index] || { options: [] };
    const requiresExplanation = question.options.some(
      opt => answer.options.includes(opt._id) && opt.requireExplanation
    );

    if (!requiresExplanation) return null;

    const explanation = explanations.find(ex => ex._id === question._id) || { text: "" };

    return (
      <div className="mb-10">
        <label className="form-label ">Please specify</label>
        <textarea
          className="input_text metropill"
          rows="3"
          value={explanation.text}
          placeholder="Type here..."
          onChange={(e) => handleExplanationChange(question._id, e.target.value)}
        ></textarea>
      </div>
    );
  };

  const renderErrorSection = () => {
    return (
      <div className="alert alert-danger mt-3">
        <h5>This vaccine may not be suitable for you</h5>
        <p>Please contact us for further advice:</p>
        <ul className="mb-0">
          <li>Email: <a href="mailto:info@myprivatechemist.com">info@myprivatechemist.com</a></li>
          <li>Phone: <a href="tel:02039879202">020 3987 9202</a></li>
        </ul>
      </div>
    );
  };

  const isCurrentQuestionAnswered = () => {
    if (currentQuestionIndex >= questions.length) return true;

    const question = questions[currentQuestionIndex];
    const currentAnswer = answers[currentQuestionIndex];

    if (!question) return true;

    const isAnswered =
      ((question.inputType === "radio" ||
        question.inputType === "select" ||
        question.inputType === "checkbox") &&
        Array.isArray(currentAnswer?.options) &&
        currentAnswer.options.length > 0) ||
      (question.inputType === "text" &&
        typeof currentAnswer?.description === "string" &&
        currentAnswer.description.trim().length > 0);

    if (!isAnswered) return false;

    const hasCorrectOptions = question.options?.some(
      (option) => option.isCorrectAnswer === true
    );

    const requiresExplanation =
      question.inputType !== "text" &&
      currentAnswer?.options?.some((optionId) =>
        question.options.find((option) => option._id === optionId)
          ?.requireExplanation
      );

    const hasExplanation = explanations?.some(
      (explanation) =>
        explanation._id === question._id &&
        typeof explanation.text === "string" &&
        explanation.text.trim().length > 0
    );

    if (requiresExplanation && !hasExplanation) {
      return false;
    }

    let hasIncorrectOption = false;
    if (hasCorrectOptions) {
      hasIncorrectOption = currentAnswer?.options?.some(
        (optionId) =>
          question.options.find((option) => option._id === optionId)
            ?.isCorrectAnswer === false
      );
    }
    return !hasIncorrectOption;
  };

  const isSubmitDisabled = () => {
    return questions.some((question, index) => {
      const currentAnswer = answers[index];

      if (!question) return true;

      const isAnswered =
        ((question.inputType === "radio" ||
          question.inputType === "select" ||
          question.inputType === "checkbox") &&
          Array.isArray(currentAnswer?.options) &&
          currentAnswer.options.length > 0) ||
        (question.inputType === "text" &&
          typeof currentAnswer?.description === "string" &&
          currentAnswer.description.trim().length > 0);

      const hasCorrectOptions = question.options?.some(
        (option) => option.isCorrectAnswer === true
      );

      const requiresExplanation =
        question.inputType !== "text" &&
        currentAnswer?.options?.some((optionId) =>
          question.options.find((option) => option._id === optionId)
            ?.requireExplanation
        );

      const hasExplanation = explanations?.some(
        (explanation) =>
          explanation._id === question._id &&
          typeof explanation.text === "string" &&
          explanation.text.trim().length > 0
      );

      if (requiresExplanation && !hasExplanation) {
        return true;
      }

      let hasIncorrectOption = false;
      if (hasCorrectOptions) {
        hasIncorrectOption = currentAnswer?.options?.some(
          (optionId) =>
            question.options.find((option) => option._id === optionId)
              ?.isCorrectAnswer === false
        );
      }
      return !isAnswered || hasIncorrectOption;
    });
  };

  const handleNextQuestion = () => {
    if (isCurrentQuestionAnswered() && currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleConsultationComplete = () => {
    setIsLoading(true);
    dispatch(
      saveVaccineConsultation({
        answers: answers,
        explanations: explanations,
        formId: form?._id,
      })
    );

    setTimeout(() => {
      handleClose();
      setIsLoading(false);
    }, 500);
  };



  return (
    <Modal
      show={showConsultation}
      className=""
      onHide={handleClose}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Travel vaccine risk assessment</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "500px", overflowY: "auto", padding: "20px" }}>
        <div>
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="metropill" style={{ fontSize: "14px", fontWeight: "500" }}>
                Question {currentQuestionIndex + 1} of {questions.length}
              </span>
            </div>
            <div className="progress" style={{ height: "8px" }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{
                  width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
                  backgroundColor: "#e2b9b3"
                }}
                aria-valuenow={currentQuestionIndex + 1}
                aria-valuemin="0"
                aria-valuemax={questions.length}
              ></div>
            </div>
          </div>

          {/* Current Question */}
          {questions[currentQuestionIndex] && (
            <React.Fragment>
              {renderQuestionInput(questions[currentQuestionIndex], currentQuestionIndex)}
              {renderExplanationField(questions[currentQuestionIndex], currentQuestionIndex)}
            </React.Fragment>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="px-0">
        <button
          className="btncust w-auto activebg cursor-pointer btncust-light"
          onClick={handleClose}
        >
          Close
        </button>

        <button
          className="btncust w-auto activebg cursor-pointer"
          onClick={handlePreviousQuestion}
          disabled={currentQuestionIndex === 0}
          style={{ cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer', opacity: currentQuestionIndex === 0 ? 0.5 : 1 }}
        >
          Previous
        </button>

        {currentQuestionIndex < questions.length - 1 ? (
          <button
            className={`w-auto activebg cursor-pointer m-0 ${isCurrentQuestionAnswered() ? 'btncust' : 'btn-disable'}`}
            onClick={handleNextQuestion}
            disabled={!isCurrentQuestionAnswered()}
            style={{ cursor: !isCurrentQuestionAnswered() ? 'not-allowed' : 'pointer' }}
          >
            Next
          </button>
        ) : (
          <button
            className={`w-auto activebg cursor-pointer m-0 ${isSubmitDisabled() ? 'btn-disable' : 'btncust'}`}
            onClick={() => {
              handleConsultationComplete();
            }}
            disabled={isSubmitDisabled()}
            style={{ cursor: isSubmitDisabled() ? 'not-allowed' : 'pointer' }}
          >
            Submit
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default VaccineAssessmentModal;